import { Helmet } from 'react-helmet-async';
import { useSiteContent } from '../context/SiteContentContext';

// Default SEO configuration
const defaultConfig = {
  siteName: 'Tetralog Ltd.',
  siteUrl: 'https://www.tetralog.bg',
  defaultImage: 'https://www.tetralog.bg/images/og-image.jpg',
  twitterHandle: '@tetralog',
  locale: 'bg_BG',
  localeAlternate: 'en_US',
};

// Page-specific SEO data
const pageSeoData = {
  home: {
    en: {
      title: 'Tetralog Ltd. | International Transport & Logistics',
      description: 'Your trusted partner for international transport and logistics. Over 20 years of experience in cargo transport across Europe. Express delivery, groupage, specialized cargo.',
      keywords: 'international transport, logistics Bulgaria, cargo transport Europe, express delivery, groupage transport, freight forwarding',
    },
    bg: {
      title: 'Тетралог ЕООД | Международен Транспорт и Логистика',
      description: 'Вашият доверен партньор за международен транспорт и логистика. Над 20 години опит в превоза на товари в цяла Европа. Експресни доставки, групажи, специализирани товари.',
      keywords: 'международен транспорт, логистика България, карго транспорт Европа, експресни доставки, групажен транспорт, спедиция',
    },
  },
  about: {
    en: {
      title: 'About Us | Tetralog Ltd.',
      description: 'Learn about Tetralog Ltd. - a leading transport and logistics company in Bulgaria with over 20 years of experience. Our history, values, and commitment to excellence.',
      keywords: 'about Tetralog, transport company Bulgaria, logistics company Pazardzhik, company history, transport experience',
    },
    bg: {
      title: 'За Нас | Тетралог ЕООД',
      description: 'Научете повече за Тетралог ЕООД - водеща транспортна и логистична компания в България с над 20 години опит. Нашата история, ценности и ангажимент към съвършенство.',
      keywords: 'за Тетралог, транспортна компания България, логистична фирма София, история на компанията, опит в транспорта',
    },
  },
  services: {
    en: {
      title: 'Our Services | Tetralog Ltd.',
      description: 'Comprehensive transport and logistics services: international moving, express transportation, groupage, specialized cargo, customs clearance, and warehousing.',
      keywords: 'transport services, logistics services, international moving, express transport, groupage services, specialized cargo, customs clearance',
    },
    bg: {
      title: 'Нашите Услуги | Тетралог ЕООД',
      description: 'Пълна гама транспортни и логистични услуги: международни премествания, експресен транспорт, групажи, специализирани товари, митническо оформяне и складиране.',
      keywords: 'транспортни услуги, логистични услуги, международни премествания, експресен транспорт, групажни услуги, специализирани товари',
    },
  },
  fleet: {
    en: {
      title: 'Our Fleet | Tetralog Ltd.',
      description: 'Modern fleet of vehicles for all your transport needs. From small vans to large trucks, all equipped with GPS tracking and temperature control.',
      keywords: 'transport fleet, trucks Bulgaria, cargo vehicles, temperature controlled transport, GPS tracking',
    },
    bg: {
      title: 'Нашият Флот | Тетралог ЕООД',
      description: 'Модерен автопарк за всички ваши транспортни нужди. От малки бусове до големи камиони, всички оборудвани с GPS проследяване и температурен контрол.',
      keywords: 'транспортен флот, камиони България, товарни превозни средства, хладилен транспорт, GPS проследяване',
    },
  },
  destinations: {
    en: {
      title: 'Destinations | Tetralog Ltd.',
      description: 'We deliver across Europe and beyond. Regular routes to Germany, France, Italy, Spain, UK, and more. Check our destination coverage.',
      keywords: 'transport destinations, Europe delivery, Germany transport, France logistics, Italy cargo, international routes',
    },
    bg: {
      title: 'Дестинации | Тетралог ЕООД',
      description: 'Доставяме в цяла Европа и отвъд. Редовни маршрути до Германия, Франция, Италия, Испания, Великобритания и други. Проверете нашето покритие.',
      keywords: 'транспортни дестинации, доставки Европа, транспорт Германия, логистика Франция, карго Италия, международни маршрути',
    },
  },
  'why-us': {
    en: {
      title: 'Why Choose Us | Tetralog Ltd.',
      description: 'Why choose Tetralog? Reliable service, competitive prices, experienced team, modern fleet, real-time tracking, and 24/7 customer support.',
      keywords: 'why choose Tetralog, reliable transport, competitive prices, experienced logistics, customer support',
    },
    bg: {
      title: 'Защо Да Изберете Нас | Тетралог ЕООД',
      description: 'Защо да изберете Тетралог? Надеждна услуга, конкурентни цени, опитен екип, модерен флот, проследяване в реално време и 24/7 поддръжка.',
      keywords: 'защо Тетралог, надежден транспорт, конкурентни цени, опитна логистика, клиентска поддръжка',
    },
  },
  careers: {
    en: {
      title: 'Careers | Tetralog Ltd.',
      description: 'Join our team! Explore career opportunities at Tetralog Ltd. We are looking for drivers, logistics specialists, and office staff.',
      keywords: 'careers Tetralog, jobs transport Bulgaria, driver jobs, logistics jobs Pazardzhik, transport careers',
    },
    bg: {
      title: 'Кариери | Тетралог ЕООД',
      description: 'Присъединете се към нашия екип! Разгледайте възможностите за кариера в Тетралог ЕООД. Търсим шофьори, логистични специалисти и офис персонал.',
      keywords: 'кариери Тетралог, работа транспорт България, работа шофьор, логистика работа София, транспортни кариери',
    },
  },
  news: {
    en: {
      title: 'News | Tetralog Ltd.',
      description: 'Latest news and updates from Tetralog Ltd. Stay informed about our services, industry news, and company announcements.',
      keywords: 'Tetralog news, transport news Bulgaria, logistics updates, company announcements',
    },
    bg: {
      title: 'Новини | Тетралог ЕООД',
      description: 'Последни новини и актуализации от Тетралог ЕООД. Бъдете информирани за нашите услуги, новини от индустрията и фирмени съобщения.',
      keywords: 'новини Тетралог, транспортни новини България, логистични актуализации, фирмени съобщения',
    },
  },
  faq: {
    en: {
      title: 'FAQ | Tetralog Ltd.',
      description: 'Frequently asked questions about our transport and logistics services. Find answers about pricing, delivery times, tracking, and more.',
      keywords: 'FAQ transport, logistics questions, delivery FAQ, shipping questions, transport answers',
    },
    bg: {
      title: 'Често Задавани Въпроси | Тетралог ЕООД',
      description: 'Често задавани въпроси за нашите транспортни и логистични услуги. Намерете отговори за цени, срокове за доставка, проследяване и други.',
      keywords: 'ЧЗВ транспорт, логистични въпроси, въпроси доставка, спедиторски въпроси, транспортни отговори',
    },
  },
  contact: {
    en: {
      title: 'Contact Us | Tetralog Ltd.',
      description: 'Get in touch with Tetralog Ltd. Contact us for quotes, inquiries, or support. Office in Pazardzhik, Bulgaria. Phone, email, and contact form available.',
      keywords: 'contact Tetralog, transport inquiry, logistics contact, Pazardzhik office, get quote',
    },
    bg: {
      title: 'Контакти | Тетралог ЕООД',
      description: 'Свържете се с Тетралог ЕООД. Пишете ни за оферти, запитвания или поддръжка. Офис в София, България. Телефон, имейл и контактна форма.',
      keywords: 'контакти Тетралог, транспортно запитване, логистика контакт, офис София, поискай оферта',
    },
  },
  'ldm-calculator': {
    en: {
      title: 'Cargo Space Calculator | Tetralog Ltd.',
      description: 'Free cargo space calculator. Calculate loading meters (LDM), volume, and optimize your shipment. Plan your cargo efficiently.',
      keywords: 'cargo calculator, LDM calculator, loading meters, shipment calculator, cargo space, transport calculator',
    },
    bg: {
      title: 'Калкулатор Товарно Пространство | Тетралог ЕООД',
      description: 'Безплатен калкулатор за товарно пространство. Изчислете товарни метри (LDM), обем и оптимизирайте пратката си. Планирайте товара ефективно.',
      keywords: 'карго калкулатор, LDM калкулатор, товарни метри, калкулатор пратки, товарно пространство, транспортен калкулатор',
    },
  },
  'privacy-policy': {
    en: {
      title: 'Privacy Policy | Tetralog Ltd.',
      description: 'Privacy Policy of Tetralog Ltd. Learn how we collect, use, and protect your personal data.',
      keywords: 'privacy policy, data protection, GDPR, personal data',
    },
    bg: {
      title: 'Политика за Поверителност | Тетралог ЕООД',
      description: 'Политика за поверителност на Тетралог ЕООД. Научете как събираме, използваме и защитаваме вашите лични данни.',
      keywords: 'политика поверителност, защита данни, GDPR, лични данни',
    },
  },
  'terms-of-service': {
    en: {
      title: 'Terms of Service | Tetralog Ltd.',
      description: 'Terms of Service for Tetralog Ltd. Read our terms and conditions for using our transport and logistics services.',
      keywords: 'terms of service, terms and conditions, service agreement',
    },
    bg: {
      title: 'Общи Условия | Тетралог ЕООД',
      description: 'Общи условия на Тетралог ЕООД. Прочетете нашите условия за ползване на транспортни и логистични услуги.',
      keywords: 'общи условия, условия за ползване, договор за услуги',
    },
  },
  'cookie-policy': {
    en: {
      title: 'Cookie Policy | Tetralog Ltd.',
      description: 'Cookie Policy of Tetralog Ltd. Learn about the cookies we use and how to manage your preferences.',
      keywords: 'cookie policy, cookies, website cookies, cookie preferences',
    },
    bg: {
      title: 'Политика за Бисквитки | Тетралог ЕООД',
      description: 'Политика за бисквитки на Тетралог ЕООД. Научете за бисквитките, които използваме и как да управлявате предпочитанията си.',
      keywords: 'политика бисквитки, cookies, уебсайт бисквитки, предпочитания бисквитки',
    },
  },
};

export const SEO = ({ page = 'home', customTitle, customDescription, customImage, noIndex = false }) => {
  const { language } = useSiteContent();
  
  // Get page-specific SEO data
  const pageData = pageSeoData[page]?.[language] || pageSeoData.home[language];
  
  const title = customTitle || pageData.title;
  const description = customDescription || pageData.description;
  const keywords = pageData.keywords;
  const image = customImage || defaultConfig.defaultImage;
  
  // Build proper URLs based on language
  const pagePath = page === 'home' ? '' : page;
  const bgUrl = `${defaultConfig.siteUrl}/${pagePath}`;
  const enUrl = `${defaultConfig.siteUrl}/en${pagePath ? '/' + pagePath : ''}`;
  const currentUrl = language === 'en' ? enUrl : bgUrl;
  
  const locale = language === 'bg' ? defaultConfig.locale : defaultConfig.localeAlternate;
  const alternateLocale = language === 'bg' ? defaultConfig.localeAlternate : defaultConfig.locale;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Language alternates */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="bg" href={bgUrl} />
      <link rel="alternate" hrefLang="x-default" href={bgUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={defaultConfig.siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional Meta */}
      <meta name="author" content="Tetralog Ltd." />
      <meta name="geo.region" content="BG" />
      <meta name="geo.placename" content="Pazardzhik" />
      <meta name="geo.position" content="42.1928;24.3336" />
      <meta name="ICBM" content="42.1928, 24.3336" />
    </Helmet>
  );
};

export default SEO;
